import type { DashboardCountDto, DashboardHourDto, DashboardSoFar } from '@his/shared';

export function changePct({ today, lastWeek }: DashboardSoFar): number | null {
  if (lastWeek <= 0) return null;
  return Math.round(((today - lastWeek) / lastWeek) * 100);
}

export function partOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  return 'evening';
}

export function hourInZone(timeZone: string, now: Date = new Date()): number {
  const hour = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    timeZone,
  }).format(now);
  return Number(hour) % 24;
}

export function busiestHour(hours: DashboardHourDto[]): DashboardHourDto | null {
  let best: DashboardHourDto | null = null;
  for (const hour of hours) {
    if (hour.typical > 0 && (!best || hour.typical > best.typical)) best = hour;
  }
  return best;
}

export function activeHourWindow(
  hours: DashboardHourDto[],
  currentHour: number,
): DashboardHourDto[] {
  const active = hours
    .filter((hour) => hour.today > 0 || hour.typical > 0)
    .map((hour) => hour.hour);
  const first = Math.max(0, Math.min(8, currentHour, ...active) - 1);
  const last = Math.min(23, Math.max(18, currentHour, ...active) + 1);
  return hours.filter((hour) => hour.hour >= first && hour.hour <= last);
}

export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export type OccupancyLevel = 'calm' | 'busy' | 'full';

export function occupancyLevel(pct: number): OccupancyLevel {
  if (pct >= 90) return 'full';
  if (pct >= 75) return 'busy';
  return 'calm';
}

export const QUEUE_STAGES = [
  { key: 'expected', label: 'Yet to arrive', statuses: ['booked', 'confirmed'] },
  { key: 'waiting', label: 'Waiting', statuses: ['arrived'] },
  { key: 'with_doctor', label: 'With doctor', statuses: ['in_consultation'] },
  { key: 'seen', label: 'Seen', statuses: ['completed'] },
  { key: 'no_show', label: 'No-show', statuses: ['no_show'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
] as const;

export type QueueStageKey = (typeof QUEUE_STAGES)[number]['key'];

export function queueStages(
  counts: DashboardCountDto[],
): { key: QueueStageKey; label: string; count: number }[] {
  const byStatus = new Map(counts.map((row) => [row.key, row.count]));
  return QUEUE_STAGES.map((stage) => ({
    key: stage.key,
    label: stage.label,
    count: stage.statuses.reduce((sum, status) => sum + (byStatus.get(status) ?? 0), 0),
  }));
}

export function sumBy<T>(rows: readonly T[], pick: (row: T) => number): number {
  return rows.reduce((sum, row) => sum + pick(row), 0);
}

export function paceSentence(noun: string, soFar: DashboardSoFar): string {
  const count = `${soFar.today} ${soFar.today === 1 ? noun : `${noun}s`} so far`;
  const change = changePct(soFar);
  if (soFar.today === 0 && soFar.lastWeek === 0) return `No ${noun}s yet today.`;
  if (change === null) return `${count} today.`;
  if (Math.abs(change) < 5) return `${count} — about the same as this time last week.`;
  return `${count} — ${Math.abs(change)}% ${change > 0 ? 'busier' : 'quieter'} than this time last week.`;
}
